import { defineStore } from 'pinia';
import { InitGoogleLibraries, maps, markers } from '@/utils/google-libraries';
import { computed, effectScope, EffectScope, ref, watch } from 'vue';
import { useLocation } from '../location.store';
import { PropertyService } from '@/http-services/property.service';
import { GetEquipmentPinSrc, NetworkEquipmentMarker, PropertyMarker } from './markers';
import { Distance } from '@/views/map/utils';
import { PropertyCoordinates, PropertyRow } from 'common';
import {
  Cluster,
  ClusterStats,
  Marker,
  MarkerClusterer,
  Renderer,
  SuperClusterAlgorithm,
} from '@googlemaps/markerclusterer';
import { computedAsync } from '@vueuse/core';
import { useSelectedProperty } from '../selected-property.store';
import { GatewayModel, NextLinkModel, Property, RepeaterModel } from '@ncss/models/dist/v3';
import { useIonRouter } from '@ionic/vue';
import { useRoute } from 'vue-router';

const PAGE_SIZE = 10_000;
const DASHBOARD_ZOOM = 13;
const DEFAULT_NUMBER_OF_VISIBLE_PROPERTIES = 5;

export const useNewMap = defineStore('google-map', () => {
  const modalExpandState = ref<1 | 2 | 3>(2);
  let abortController: AbortController | null = null;
  const selectedPropertyStore = useSelectedProperty();
  const propertyService = new PropertyService();
  const route = useRoute();
  const router = useIonRouter();
  let map: google.maps.Map;
  let clusterer: MarkerClusterer;
  const userLocation = useLocation();
  const zoom = ref(DASHBOARD_ZOOM);
  let loadingLock = 0;
  let propertyMarkers: { [propertyId: string]: PropertyMarker } = {};
  let networkEquipmentMarkers: NetworkEquipmentMarker[] = [];
  let userLocationMarker: google.maps.marker.AdvancedMarkerElement | undefined = undefined;
  const propertyIdsInBounds = ref<string[]>([]);
  const showNumberOfVisibleProperties = ref(DEFAULT_NUMBER_OF_VISIBLE_PROPERTIES);
  const canShowMoreVisibleProperties = computed<boolean>(
    () => propertyIdsInBounds.value.length > showNumberOfVisibleProperties.value
  );
  let selectedPropertyScope: EffectScope | undefined = undefined;
  const visibleProperties = computedAsync(async () => {
    const count = showNumberOfVisibleProperties.value;
    const ids: string[] = propertyIdsInBounds.value.slice(0, count);
    const idsToSearch: string[] = [];
    ids.forEach((id) => {
      if (!propertyMarkers[id] || !propertyMarkers[id].propertyRow) {
        idsToSearch.push(id);
      }
    });
    const props = await propertyService.findAllForUser({
      ids: idsToSearch,
      page: 0,
    });
    for (const p of props) {
      if (propertyMarkers[p._id]) {
        propertyMarkers[p._id].propertyRow = p;
      }
    }
    return ids.map((id) => propertyMarkers[id].propertyRow) as PropertyRow[];
  }, []);

  function onPropertyRouteChange() {
    upsertGatewaymarker(selectedPropertyStore.property, selectedPropertyStore.property?.gateway);
    selectedPropertyStore.property?.repeaters?.forEach((r) => upsertRepeaterMarker(selectedPropertyStore.property, r));
  }

  function onSelectedPropertyChange(property?: Property | null, old?: Property | null) {
    networkEquipmentMarkers.forEach((m) => (m.marker.map = null));
    networkEquipmentMarkers = [];
    property?.repeaters?.forEach((r) => upsertRepeaterMarker(property, r));
    upsertGatewaymarker(property, property?.gateway);
    const coords = property?.address?.location?.coordinates;
    const propertyId = property?._id;
    const bounds = map?.getBounds();
    if (coords && propertyId) {
      const { m, isNew } = upsertPropertyMarker(propertyId, coords, bounds);
      if (isNew) {
        clusterer?.addMarker(m.marker);
      }
      const latLng = new google.maps.LatLng({ lat: coords[1], lng: coords[0] });
      const sw = new google.maps.LatLng({ lat: latLng.lat() - 0.0005, lng: latLng.lng() - 0.0005 });
      const ne = new google.maps.LatLng({ lat: latLng.lat() + 0.0005, lng: latLng.lng() + 0.0005 });
      const b = new google.maps.LatLngBounds(sw, ne);
      networkEquipmentMarkers.forEach((m) => {
        if (m.marker.position) {
          b.extend(m.marker.position);
        }
      });
      map.fitBounds(b, 40);
    }

    const oldCoords = old?.address?.location?.coordinates;
    const oldId = old?._id;
    if (oldCoords && oldId && bounds) {
      const { m, isNew } = upsertPropertyMarker(oldId, oldCoords, bounds);
      if (isNew) {
        clusterer?.addMarker(m.marker);
      }
    }
  }

  async function upsertUserLocationMarker() {
    const coords = await userLocation.getCoordinates(false);
    const position = coords ? new google.maps.LatLng({ lng: coords[0], lat: coords[1] }) : null;
    if (!position && !!userLocationMarker) {
      userLocationMarker.map = null;
      userLocationMarker = undefined;
      return;
    }

    if (!userLocationMarker) {
      const div = document.createElement('div');
      div.classList.add('relative', 'flex', 'size-[18px]');
      div.innerHTML = `
        <div class="absolute inline-flex size-full animate-ping rounded-full bg-cyan-300"></div>
        <div class="relative inline-flex size-full rounded-full bg-cyan-500 border-2 border-white shadow-cyan-700 shadow-sm"></div>
      `;
      userLocationMarker = new markers.AdvancedMarkerElement({
        position,
        map,
        content: div,
        title: 'Current Location',
        zIndex: 9999,
        gmpClickable: false,
        gmpDraggable: false,
      });
    } else {
      userLocationMarker.position = position;
      userLocationMarker.map = map;
    }
  }

  function upsertGatewaymarker(property?: Property | null, gateway?: GatewayModel): NetworkEquipmentMarker | null {
    if (!gateway || !gateway.id || !property) {
      return null;
    }
    const coords = gateway.coords || [
      property.address.location.coordinates[1],
      property.address.location.coordinates[0],
    ];
    if (!coords) {
      return null;
    }
    const isSelected = route.name === 'map.gateway';
    const existing = networkEquipmentMarkers.find((m) => m.deviceId === gateway.id);
    if (!existing) {
      const pin = document.createElement('img');
      pin.src = GetEquipmentPinSrc(gateway.id, isSelected);
      pin.width = 44;
      pin.height = 40;
      const m = {
        propertyId: property._id,
        deviceId: gateway.id,
        marker: new markers.AdvancedMarkerElement({
          title: gateway.id.toString(),
          map,
          position: { lat: coords[0], lng: coords[1] },
          content: pin,
          zIndex: isSelected ? 99999 : 9999,
          gmpClickable: false,
          gmpDraggable: false,
        }),
      };
      m.marker.onclick = () => {
        router.push(`/gateway/${property._id}`);
      };
      networkEquipmentMarkers.push(m);
      return m;
    } else {
      const isHighlighted = (existing.marker.children[0] as HTMLImageElement)?.src?.includes('highlighted');
      if (isHighlighted && !isSelected) {
        (existing.marker.children[0] as HTMLImageElement).src = GetEquipmentPinSrc(gateway.id, isSelected);
        existing.marker.zIndex = 9999;
      }
      if (!isHighlighted && isSelected) {
        (existing.marker.children[0] as HTMLImageElement).src = GetEquipmentPinSrc(gateway.id, isSelected);
        existing.marker.zIndex = 99999;
      }
      return existing;
    }
  }

  function upsertRepeaterMarker(
    property?: Property,
    repeater?: NextLinkModel | RepeaterModel
  ): NetworkEquipmentMarker | null {
    if (!repeater || !repeater.id || !property) {
      return null;
    }
    const coords = repeater.coords || [
      property.address.location.coordinates[1],
      property.address.location.coordinates[0],
    ];
    if (!coords) {
      return null;
    }
    const selectedId = route.params.repeaterId ? +(route.params.repeaterId as string) : null;
    const isSelected = repeater.id === selectedId;
    const existing = networkEquipmentMarkers.find((m) => m.deviceId === repeater.id);
    if (!existing) {
      const pin = document.createElement('img');
      pin.src = GetEquipmentPinSrc(repeater.id, isSelected);
      pin.width = 44;
      pin.height = 40;
      const m = {
        propertyId: property._id,
        deviceId: repeater.id,
        marker: new markers.AdvancedMarkerElement({
          title: repeater.id.toString(),
          map,
          position: { lat: coords[0], lng: coords[1] },
          content: pin,
          zIndex: isSelected ? 99999 : 9999,
          gmpClickable: false,
          gmpDraggable: false,
        }),
      };
      m.marker.onclick = () => {
        router.push(`/repeater/${property._id}/${repeater.id}`);
      };
      networkEquipmentMarkers.push(m);
      return m;
    } else {
      const isHighlighted = (existing.marker.children[0] as HTMLImageElement)?.src?.includes('highlighted');
      if (isHighlighted && !isSelected) {
        (existing.marker.children[0] as HTMLImageElement).src = GetEquipmentPinSrc(repeater.id, isSelected);
        existing.marker.zIndex = 9999;
      }
      if (!isHighlighted && isSelected) {
        (existing.marker.children[0] as HTMLImageElement).src = GetEquipmentPinSrc(repeater.id, isSelected);
        existing.marker.zIndex = 99999;
      }
      return existing;
    }
  }

  function showMoreVisibleProperties() {
    if (canShowMoreVisibleProperties.value) {
      showNumberOfVisibleProperties.value += 5;
    }
  }

  function resetNumberOfVisibleProperties() {
    showNumberOfVisibleProperties.value = DEFAULT_NUMBER_OF_VISIBLE_PROPERTIES;
  }

  async function init(el: HTMLElement | null) {
    if (!el) {
      return;
    }
    if (!maps) {
      await InitGoogleLibraries();
    }
    propertyMarkers = {};
    networkEquipmentMarkers = [];
    map = new maps.Map(el, {
      mapId: 'ncss-mobile-map',
      zoom: zoom.value,
      disableDefaultUI: true,
      center: { lat: 41.411835, lng: -75.665245 },
      tiltInteractionEnabled: false,
      headingInteractionEnabled: false,
      minZoom: 5,
      clickableIcons: false,
    });
    clusterer = new MarkerClusterer({
      map,
      renderer: new ClusterRenderer(),
      algorithm: new SuperClusterAlgorithm({ radius: 50, minPoints: 3 }),
    });
    if (!selectedPropertyStore.propertyId) {
      await focusOnUserLocation({ alertOnFailure: false, skipAnimation: true });
    }
    selectedPropertyScope?.stop();
    selectedPropertyScope = effectScope();
    selectedPropertyScope.run(() => {
      watch(
        () => selectedPropertyStore.property,
        (property, old) => {
          onSelectedPropertyChange(property, old);
        },
        { immediate: true }
      );
      watch(
        () => route.fullPath,
        () => {
          onPropertyRouteChange();
        },
        { immediate: true }
      );
      watch(
        () => userLocation.coordinates,
        () => {
          upsertUserLocationMarker();
        },
        { immediate: true }
      );
    });
    map.addListener('idle', () => {
      const b = map.getBounds();
      if (!b) {
        return;
      }
      loadNearbyProperties(b);
    });
  }

  async function focusOnUserLocation(opts: { skipAnimation?: boolean; alertOnFailure?: boolean } = {}) {
    const coords = await userLocation.getCoordinates(opts?.alertOnFailure);
    if (coords?.length) {
      setCamera({
        center: new google.maps.LatLng({ lat: coords[1], lng: coords[0] }),
        zoom: DASHBOARD_ZOOM,
        skipAnimation: opts.skipAnimation,
      });
    }
  }

  function setCamera(opts: { center: google.maps.LatLng; zoom: number; skipAnimation?: boolean }) {
    if (opts?.skipAnimation) {
      map?.moveCamera({
        center: opts.center,
        zoom: opts.zoom,
      });
    } else {
      map?.panTo(opts.center);
      map?.setZoom(opts.zoom);
    }
  }

  async function loadNearbyProperties(bounds: google.maps.LatLngBounds) {
    if (!markers) {
      await InitGoogleLibraries();
    }
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();
    loadingLock++;
    const myLock = loadingLock;
    let page = 0;
    let propertyCoords: PropertyCoordinates[] = [];
    do {
      if (myLock !== loadingLock) {
        break;
      }
      propertyCoords = await fetchPropertyCoordinates(
        bounds.getCenter(),
        bounds.getNorthEast(),
        page,
        abortController.signal
      );
      const newMarkers: google.maps.marker.AdvancedMarkerElement[] = [];
      for (const p of propertyCoords) {
        const { isNew, m } = upsertPropertyMarker(p._id, p.coords, bounds);
        if (isNew) {
          newMarkers.push(m.marker);
        }
      }
      if (newMarkers.length) {
        clusterer.addMarkers(newMarkers);
      }
      page++;
    } while (propertyCoords.length >= PAGE_SIZE);
    // cleanup no longer visible markers and emit
    if (myLock === loadingLock) {
      const markersToRemove: PropertyMarker[] = [];
      const markersInBounds: PropertyMarker[] = [];
      Object.keys(propertyMarkers).forEach((propertyId) => {
        const position = propertyMarkers[propertyId]?.marker.position;
        if (position && bounds.contains(position)) {
          markersInBounds.push(propertyMarkers[propertyId]);
        }
        if (position && !bounds.contains(position)) {
          markersToRemove.push(propertyMarkers[propertyId]);
          delete propertyMarkers[propertyId];
        }
      });
      if (markersToRemove.length) {
        clusterer.removeMarkers(markersToRemove.map((m) => m.marker));
      }
      markersInBounds.sort((a, b) => (a.distanceFromMapCenter || 0) - (b.distanceFromMapCenter || 0));
      propertyIdsInBounds.value = markersInBounds.map((m) => m.propertyId);
    }
  }

  function fetchPropertyCoordinates(
    center: google.maps.LatLng,
    northEast: google.maps.LatLng,
    page: number,
    signal?: AbortSignal
  ) {
    return propertyService.findNearbyCoordinates(
      {
        coords: [center.lng(), center.lat()],
        maxDistance: Math.round(
          Distance({ lat: center.lat(), lng: center.lng() }, { lat: northEast.lat(), lng: northEast.lng() }) * 1000
        ),
        page,
        pageSize: PAGE_SIZE,
      },
      signal
    );
  }

  function upsertPropertyMarker(
    propertyId: string,
    coords: [number, number] | number[],
    bounds?: google.maps.LatLngBounds | null
  ): { isNew: boolean; m: PropertyMarker } {
    let isNew = false;
    const isSelected: boolean = !!selectedPropertyStore.propertyId && selectedPropertyStore.propertyId === propertyId;
    if (!propertyMarkers[propertyId]) {
      const pin = document.createElement('img');
      pin.src = isSelected ? '/map/property-pin-highlighted.png' : '/map/property-pin.png';
      pin.width = 44;
      pin.height = 40;
      const m = new markers.AdvancedMarkerElement({
        position: { lat: coords[1], lng: coords[0] },
        content: pin,
        title: propertyId,
        gmpClickable: false,
        gmpDraggable: false,
        zIndex: isSelected ? 9999 : undefined,
      });
      m.onclick = () => {
        router.push(`/property/${m.title}`);
      };
      propertyMarkers[propertyId] = {
        propertyId: propertyId,
        distanceFromMapCenter: bounds
          ? Distance(
              { lat: coords[1], lng: coords[0] },
              { lat: bounds.getCenter().lat(), lng: bounds.getCenter().lng() }
            )
          : undefined,
        marker: m,
      };
      isNew = true;
    } else {
      const marker = propertyMarkers[propertyId];
      marker.distanceFromMapCenter = bounds
        ? Distance({ lat: coords[1], lng: coords[0] }, { lat: bounds.getCenter().lat(), lng: bounds.getCenter().lng() })
        : undefined;
      const isHighlighted = (marker.marker.children[0] as HTMLImageElement)?.src?.includes('highlighted');
      if (isSelected && !isHighlighted) {
        (marker.marker.children[0] as HTMLImageElement).src = '/map/property-pin-highlighted.png';
        marker.marker.zIndex = 9999;
      }
      if (!isSelected && isHighlighted) {
        (marker.marker.children[0] as HTMLImageElement).src = '/map/property-pin.png';
        marker.marker.zIndex = undefined;
      }
    }
    return { isNew, m: propertyMarkers[propertyId] };
  }

  function nextModalExpandState() {
    if (modalExpandState.value === 1) {
      modalExpandState.value = 2;
    } else if (modalExpandState.value === 2) {
      modalExpandState.value = 3;
    } else {
      modalExpandState.value = 1;
    }
  }

  function previousModalExpandState() {
    if (modalExpandState.value === 1) {
      modalExpandState.value = 3;
    } else if (modalExpandState.value === 2) {
      modalExpandState.value = 1;
    } else {
      modalExpandState.value = 2;
    }
  }

  function onPropertyDeleted(id: string) {
    propertyIdsInBounds.value = propertyIdsInBounds.value.filter((v) => v !== id);
  }

  return {
    init,
    focusOnUserLocation,
    onPropertyDeleted,
    visibleProperties,
    canShowMoreVisibleProperties,
    resetNumberOfVisibleProperties,
    showMoreVisibleProperties,
    modalExpandState,
    nextModalExpandState,
    previousModalExpandState,
  };
});

class ClusterRenderer implements Renderer {
  render(cluster: Cluster, stats: ClusterStats, map: google.maps.Map): Marker {
    const div = document.createElement('div');
    div.style.position = 'relative';
    const img = document.createElement('img');
    img.width = 44;
    img.height = 55;
    img.src = '/map/property-cluster-pin.png';
    div.appendChild(img);
    const text = document.createElement('div');
    text.style.position = 'absolute';
    text.style.width = '100%';
    text.style.textAlign = 'center';
    text.style.bottom = '11px';
    text.style.fontSize = '10px';
    text.style.color = '#e3515f';
    text.style.fontFamily = 'Rubik';
    text.style.fontWeight = '700';
    text.innerHTML = `x${cluster.markers?.length}`;
    div.appendChild(text);
    return new google.maps.marker.AdvancedMarkerElement({
      position: cluster.position,
      content: div,
      gmpClickable: false,
      gmpDraggable: false,
      zIndex: cluster.markers?.length,
    });
  }
}
